import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'

@Component({
    selector: 'eb-datalist',
    templateUrl: './eb-datalist.component.html',
    styleUrls: ['./eb-datalist.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: EbDatalistComponent
        }
    ],
    standalone: false
})
export class EbDatalistComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() placeholder = ''
  @Input() readonly = false
  @Input() error = false
  @Input() disabled = false
  @Input() positionDropdownLeft = false

  constructor(
    protected hostElementRef: ElementRef,
    protected cd: ChangeDetectorRef
  ) {
    this.hostElement = hostElementRef.nativeElement
  }

  private get inputElement(): HTMLInputElement {
    return this.hostElement.querySelector('input')
  }
  private get datalistElement(): HTMLDataListElement {
    return this.hostElement.querySelector('datalist')
  }
  private get optionElements(): HTMLOptionElement[] {
    return this.querySelectAsArray('option')
  }
  private get matchingOptionElements(): HTMLOptionElement[] {
    return this.querySelectAsArray('option:not(.divergent)')
  }
  private get focussedOption(): HTMLOptionElement {
    return this.datalistElement.querySelector('option.focus:not(.divergent)')
  }
  private get optionByCurrentValue(): HTMLOptionElement {
    return this.optionElements.find(
      option => EbDatalistComponent.getOptionValue(option) === this.value
    )
  }

  private readonly hostElement: HTMLElement
  private observer
  private value
  private keepFocus = false
  touched = false
  disabledState = false

  private static getOptionText(option: HTMLOptionElement) {
    return (option.innerText || option.text || '').trim() || option.getAttribute('value')
  }
  private static getOptionValue(option: HTMLOptionElement) {
    return option.hasAttribute('value') ? option.getAttribute('value') : option.innerText.trim()
  }

  onChange = value => {
    /* intentionally empty */
  }
  onTouched = () => {
    /* intentionally empty */
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched
  }

  registerOnChange(onChange: any) {
    this.onChange = onChange
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched()
      this.touched = true
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabledState = disabled
    this.cd.detectChanges()
  }

  writeValue(value) {
    this.value = value
    this.handleValueAssignment()
  }

  onTextInput() {
    const text = this.inputElement.value.toUpperCase()
    this.showList()
    this.optionElements.forEach(option => {
      const innerText = EbDatalistComponent.getOptionText(option)
      const value = EbDatalistComponent.getOptionValue(option)
      if (value.toUpperCase().indexOf(text) > -1 || innerText.toUpperCase().indexOf(text) > -1) {
        option.classList.remove('divergent')
        option.classList.add('matching')
      } else {
        option.classList.add('divergent')
        option.classList.remove('matching')
      }
    })
    this.checkDivergentFocus()
    this.checkSingleMatch()
    this.scrollFocussedOptionIntoView()
  }
  private checkDivergentFocus() {
    const divergent = this.datalistElement.querySelector('option.divergent.focus')
    divergent?.classList.remove('focus')
    if (divergent) {
      this.findMatchingSibling(divergent, -1)?.classList.add('focus')
    }
  }
  private checkSingleMatch() {
    const matchingOptions = this.matchingOptionElements
    if (matchingOptions.length === 1) {
      this.focusOption(matchingOptions[0])
    }
  }

  onInputChange() {
    const option = this.getOptionForInputText()
    if (option) {
      this.value = EbDatalistComponent.getOptionValue(option)
    } else {
      this.inputElement.value = ''
      this.value = ''
      this.resetOptions()
    }
    this.onChange(this.value)
  }

  onEnter(event) {
    event.preventDefault()
    const option = this.focussedOption || this.getOptionForInputText()
    option?.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }))
    this.onInputChange()
    this.hideList()
  }
  onUpDown(event, delta) {
    event.preventDefault()
    this.showList()
    const next = this.findNextOptionToFocus(delta)
    if (next) {
      this.focusOption(next)
    }
  }

  onInputMousedown() {
    this.showList()
  }

  onListMousedown(event) {
    const target: Element = event.target
    if (/^option$/i.test(target.tagName)) {
      this.onListSelect(target)
    } else {
      this.keepFocus = true
      requestAnimationFrame(() => {
        this.keepFocus = false
        this.inputElement.focus()
      })
    }
  }

  onFocus() {
    this.showList()
    this.markAsTouched()
  }

  onBlur() {
    if (!this.keepFocus) {
      this.hideList()
    }
  }

  private onListSelect(option) {
    this.inputElement.value = EbDatalistComponent.getOptionText(option)
    this.value = EbDatalistComponent.getOptionValue(option)
    this.onChange(this.value)
    this.hideList()
  }

  private querySelectAsArray(query: string): HTMLOptionElement[] {
    return Array.from(this.datalistElement.querySelectorAll(query))
  }
  private getOptionForInputText() {
    return this.optionElements.find(
      option => EbDatalistComponent.getOptionText(option) === this.inputElement.value
    )
  }

  private focusOption(toFocus) {
    const focussed = this.focussedOption
    focussed?.classList?.remove('focus')
    if (toFocus) {
      toFocus.classList.add('focus')
    }
    this.scrollFocussedOptionIntoView()
  }
  private findNextOptionToFocus(delta: number) {
    if (!this.focussedOption) {
      if (delta > 0) {
        return this.matchingOptionElements[0]
      } else {
        return this.matchingOptionElements.slice(-1)[0]
      }
    }
    return this.findMatchingSibling(this.focussedOption, delta)
  }
  private findMatchingSibling(option, delta: number): HTMLOptionElement | undefined {
    const currentIndex = this.matchingOptionElements.indexOf(option)
    const nextIndex = currentIndex + delta
    return this.matchingOptionElements[
      Math.max(0, Math.min(nextIndex, this.matchingOptionElements.length - 1))
    ]
  }

  ngOnInit() {
    this.observer = new MutationObserver(() => {
      this.handleValueAssignment()
    })
    this.observer.observe(this.datalistElement, { childList: true })
  }

  ngOnDestroy() {
    this.observer.disconnect()
  }

  private handleValueAssignment() {
    if (this.value !== undefined && this.optionElements.length) {
      const option = this.optionByCurrentValue
      let text = this.value
      if (option) {
        text = EbDatalistComponent.getOptionText(option)
        this.focusOption(option)
      }
      this.inputElement.value = text
      this.setCurrentOptionSelected()
    }
  }

  private showList() {
    if (!this.hostElement.classList.contains('expanded') && !this.readonly && !this.disabled) {
      this.hostElement.classList.add('expanded')
      this.focusOption(this.optionByCurrentValue)
      this.scrollFocussedOptionIntoView()
      this.setCurrentOptionSelected()
    }
  }

  private hideList() {
    requestAnimationFrame(() => {
      if (this.hostElement.classList.contains('expanded')) {
        this.hostElement.classList.remove('expanded')
        this.resetOptions()
      }
    })
  }

  private resetOptions() {
    this.optionElements.forEach(option => option.classList.remove('divergent', 'focus'))
  }

  private scrollFocussedOptionIntoView() {
    const option = this.focussedOption
    const optionTop = option?.getBoundingClientRect()?.top
    if (option && optionTop) {
      const list = this.datalistElement
      const listTop = list?.getBoundingClientRect()?.top
      const listScroll = list.scrollTop
      const centerList = list.clientHeight / 2
      const centerOption = option.clientHeight / 2
      const relativeTop = optionTop + listScroll - listTop
      const centeredY = relativeTop - centerList + centerOption
      this.datalistElement.scroll({
        top: centeredY
      })
    }
  }
  private setCurrentOptionSelected() {
    this.optionElements
      .filter(opt => opt.hasAttribute('selected'))
      .forEach(opt => opt.removeAttribute('selected'))
    const option = this.optionByCurrentValue
    if (option) {
      option.setAttribute('selected', 'selected')
    }
  }
}
